// Expose React hooks as locals (Babel transpiles JSX but doesn't resolve imports)
const {
  useState,
  useCallback,
  useRef,
  useEffect
} = React;

// ── Brand tokens ───────────────────────────────────────────────────────────
const BRAND = {
  bg: "#F7F5F0",
  surface: "#FFFFFF",
  border: "#E2DDD6",
  text: "#1A1714",
  muted: "#7A7168",
  accent: "#4A7AB5",
  accentLight: "#EBF1F8",
  woodDark: "#8B5E3C",
  error: "#C0392B",
  errorBg: "#FDECEA"
};

// ── Grid spec ──────────────────────────────────────────────────────────────
// 19mm holes, 100mm center-to-center, 50mm border on all sides
// holes = floor((dim - 100) / 100) + 1
const CELL_PX = 34;
const HOLE_PX = 7;
const BORDER_PX = 17; // 50mm at CELL_PX scale
const MAX_H = 2400;
const MAX_W = 1200;
const MIN_DIM = 100;
function dimsToGrid(h, w) {
  return {
    rows: Math.max(1, Math.floor((h - 100) / 100) + 1),
    cols: Math.max(1, Math.floor((w - 100) / 100) + 1)
  };
}
function calcPrice(h, w) {
  const areaSqM = h / 1000 * (w / 1000);
  return Math.max(1, Math.round(500 * areaSqM));
}

// ── Shelf specs from plankstore ────────────────────────────────────────────
// External widths: Small 270mm, Medium 370mm, Large 470mm
// Pegs are at each end — so peg centres at ~54mm from each end (half bracket width)
// Grid spans: Small=3 holes (200mm between pegs), Medium=4 holes (300mm), Large=5 holes (400mm)
// Depth variants: 140mm or 210mm (cosmetic label only, same grid footprint)
// Prices (estimated from store starting at $47):
//   Small $47, Medium $62, Large $78  (×2 for 210mm depth)

const SHELF_SIZES = [{
  size: "small",
  label: "Small",
  colSpan: 3,
  rowSpan: 2,
  pegRowOffset: 1,
  externalW: 270,
  price140: 47,
  price210: 51
}, {
  size: "medium",
  label: "Medium",
  colSpan: 4,
  rowSpan: 2,
  pegRowOffset: 1,
  externalW: 370,
  price140: 52,
  price210: 56
}, {
  size: "large",
  label: "Large",
  colSpan: 5,
  rowSpan: 2,
  pegRowOffset: 1,
  externalW: 470,
  price140: 58,
  price210: 62
}];
const SHELF_DEPTHS = ["140mm", "210mm"];
const BOX_SIZES = [{
  size: "medium",
  label: "Medium",
  colSpan: 4,
  rowSpan: 3,
  pegRowOffset: 0,
  externalW: 370,
  externalH: 270,
  price140: 115,
  price210: 123
}, {
  size: "large",
  label: "Large",
  colSpan: 5,
  rowSpan: 3,
  pegRowOffset: 0,
  externalW: 470,
  externalH: 270,
  price140: 125,
  price210: 133
}];
const BOX_DEPTHS = ["140mm", "210mm"];
const STAIN_COLOURS = [{
  id: "neutral",
  label: "Neutral",
  hex: null,
  price: 0
}, {
  id: "whitewash",
  label: "Whitewash",
  hex: "#F5F2EE",
  price: 120
}, {
  id: "blush",
  label: "Blush",
  hex: "#E8BDB0",
  price: 120
}, {
  id: "plum",
  label: "Plum",
  hex: "#6B3A4A",
  price: 120
}, {
  id: "lavender",
  label: "Lavender",
  hex: "#8878B0",
  price: 120
}, {
  id: "pewter",
  label: "Pewter",
  hex: "#909090",
  price: 120
}, {
  id: "cerulean",
  label: "Cerulean",
  hex: "#4880B0",
  price: 120
}, {
  id: "indigo",
  label: "Indigo",
  hex: "#303870",
  price: 120
}, {
  id: "forest",
  label: "Forest",
  hex: "#304830",
  price: 120
}, {
  id: "laurel",
  label: "Laurel",
  hex: "#588058",
  price: 120
}, {
  id: "marigold",
  label: "Marigold",
  hex: "#E0A020",
  price: 120
}, {
  id: "tan",
  label: "Tan",
  hex: "#B07840",
  price: 120
}, {
  id: "hickory",
  label: "Hickory",
  hex: "#604028",
  price: 120
}, {
  id: "obsidian",
  label: "Obsidian",
  hex: "#202020",
  price: 120
}, {
  id: "umber",
  label: "Umber",
  hex: "#503820",
  price: 120
}, {
  id: "poppy",
  label: "Poppy",
  hex: "#C03020",
  price: 120
}, {
  id: "mandarin",
  label: "Mandarin",
  hex: "#E06018",
  price: 120
}, {
  id: "espresso",
  label: "Espresso",
  hex: "#201008",
  price: 120
}];
const PEG_VARIANTS = [{
  id: "d140",
  label: "D140mm",
  length: 176,
  price: 8,
  desc: "⌀19mm × 176mm"
}, {
  id: "d210",
  label: "D210mm",
  length: 246,
  price: 9,
  desc: "⌀19mm × 246mm"
}];

// ── SVG shelf renderer ─────────────────────────────────────────────────────
// Draws a 2D front-elevation of the shelf matching the product photo:
//   - Horizontal shelf plank across the top
//   - Two vertical bracket/side pieces at each end
//   - Two round pegs hanging down from the brackets (one per bracket)
//   - Peg centres align with hole grid columns
//
// w = total pixel width (colSpan * CELL_PX), h = total pixel height (rowSpan * CELL_PX)
// pegOffsetX = pixel distance from left edge to left peg centre = 0 (first cell centre = CELL_PX/2)

// ── ShelfSVG — geometry derived directly from shelf.svg ──────────────────
// SVG pitch = 283.5 units = 100mm = CELL_PX px  →  scale = CELL_PX/283.5
// Peg centres sit exactly on hole-row centres (cy = peg row * CELL_PX + CELL_PX/2)
// All shelf parts extend UPWARD from the peg row.
// The shelf occupies rowSpan=2 cells: top cell = bracket/plank, bottom cell = pegs.
//
// Per size, peg column indices within the colSpan:
//   small:  left peg at col 0, right peg at col 2  (colSpan=3, 2 holes apart)
//   medium: left peg at col 0, right peg at col 3  (colSpan=4, 3 holes apart)
//   large:  left peg at col 0, right peg at col 4  (colSpan=5, 4 holes apart)
//
// Pixel measurements (CELL_PX=34, scale=34/283.5=0.11995):
//   Peg radius:    3.23px
//   Plank x-start: 5.11px from left peg cx   (= (525.8-625)*scale + CELL_PX/2)
//   Plank y (top): -6.44px rel to peg cy      → peg_cy - 6.44
//   Plank height:  3.06px
//   Bracket width: 3.06px
//   Bracket top:   -16.81px rel to peg cy
//   Bracket height: 23.80px  (top to bottom, bottom = peg_cy + 6.99)
//   Left bracket x: 20.40px from left edge    (= (653.4-625)*scale + CELL_PX/2)
//   Right bracket x relative to its peg cx: same offset as left (20.40 - 17.00 = 3.40px inside)

function ShelfSVG({
  colSpan,
  rowSpan = 2,
  opacity = 1
}) {
  const W = colSpan * CELL_PX;
  const H = rowSpan * CELL_PX;

  // Peg column x centres (hole-aligned)
  // Left peg always at column 0 → cx = CELL_PX/2
  // Right peg column index depends on size:
  //   colSpan 3 → right at col 2;  colSpan 4 → col 3;  colSpan 5 → col 4
  const pegCX_L = CELL_PX / 2;
  const pegCX_R = W - CELL_PX / 2;

  // Peg row is the BOTTOM row of the 2-row cell
  // peg cy = top of bottom row + half cell = CELL_PX + CELL_PX/2
  const pegCY = CELL_PX + CELL_PX / 2;

  // Exact pixel values from SVG analysis
  const pegR = 3.23;
  const plankX = pegCX_L - (17.00 - 5.11); // 5.11px from left peg = pegCX_L - 11.89
  const plankW = pegCX_R + (17.00 - 5.11) * 1 - plankX + (17.00 - 5.11); // spans to mirror right
  // Simpler: plank goes from (left peg cx - 11.89) to (right peg cx + 11.89)
  const plankX1 = pegCX_L - 11.89;
  const plankX2 = pegCX_R + 11.89;
  const plankY = pegCY - 6.44;
  const plankH = 3.06;

  // Brackets: centred 3.4px inside from each peg cx
  const bW = 3.06;
  const bX_L = pegCX_L + 3.40 - bW / 2; // left bracket x
  const bX_R = pegCX_R - 3.40 - bW / 2; // right bracket x
  const bY = pegCY - 16.81; // bracket top
  const bH = 23.80; // bracket height

  // Colours
  const face = "#DFC872";
  const edge = "#B8883A";
  const pegC = "#CBA055";
  const hi = "#EDD888";
  const uid = `sh${colSpan}`;
  return /*#__PURE__*/React.createElement("svg", {
    width: W,
    height: H,
    viewBox: `0 0 ${W} ${H}`,
    style: {
      display: "block",
      opacity,
      overflow: "visible"
    },
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("filter", {
    id: uid,
    x: "-20%",
    y: "-20%",
    width: "160%",
    height: "200%"
  }, /*#__PURE__*/React.createElement("feDropShadow", {
    dx: "1.5",
    dy: "2",
    stdDeviation: "1.8",
    floodColor: "rgba(0,0,0,0.38)"
  })), /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_eg`,
    patternUnits: "userSpaceOnUse",
    x: "0",
    y: "0",
    width: bW,
    height: bH
  }, /*#__PURE__*/React.createElement("image", {
    href: ENDGRAIN_B64,
    x: -bH / 2 + bW / 2,
    y: -bW / 2 + bH / 2,
    width: bH,
    height: bW,
    preserveAspectRatio: "xMidYMid slice",
    transform: `rotate(90, ${bW / 2}, ${bH / 2})`
  })), /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_eg_pl`,
    patternUnits: "userSpaceOnUse",
    x: plankX1,
    y: plankY,
    width: plankH,
    height: plankH
  }, /*#__PURE__*/React.createElement("image", {
    href: ENDGRAIN_B64,
    x: "0",
    y: "0",
    width: plankH,
    height: plankH,
    preserveAspectRatio: "xMidYMid slice"
  }))), /*#__PURE__*/React.createElement("g", {
    filter: `url(#${uid})`
  }, /*#__PURE__*/React.createElement("rect", {
    x: bX_L,
    y: bY,
    width: bW,
    height: bH,
    fill: `url(#${uid}_eg)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: bX_R,
    y: bY,
    width: bW,
    height: bH,
    fill: `url(#${uid}_eg)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: plankX1,
    y: plankY,
    width: plankX2 - plankX1,
    height: plankH,
    fill: `url(#${uid}_eg_pl)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_peg`,
    patternUnits: "userSpaceOnUse",
    x: pegCX_L - pegR,
    y: pegCY - pegR,
    width: pegR * 2,
    height: pegR * 2
  }, /*#__PURE__*/React.createElement("image", {
    href: ASH_GRAIN_B64,
    x: "0",
    y: "0",
    width: pegR * 2,
    height: pegR * 2,
    preserveAspectRatio: "xMidYMid slice"
  })), /*#__PURE__*/React.createElement("clipPath", {
    id: `${uid}_clipL`
  }, /*#__PURE__*/React.createElement("circle", {
    cx: pegCX_L,
    cy: pegCY,
    r: pegR
  })), /*#__PURE__*/React.createElement("clipPath", {
    id: `${uid}_clipR`
  }, /*#__PURE__*/React.createElement("circle", {
    cx: pegCX_R,
    cy: pegCY,
    r: pegR
  }))), /*#__PURE__*/React.createElement("circle", {
    cx: pegCX_L,
    cy: pegCY,
    r: pegR,
    fill: `url(#${uid}_peg)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: pegCX_R,
    cy: pegCY,
    r: pegR,
    fill: `url(#${uid}_peg)`,
    stroke: edge,
    strokeWidth: "0.5"
  })));
}

// ── Mini shelf icon for sidebar ────────────────────────────────────────────
function ShelfIcon({
  colSpan
}) {
  // Render into a fixed 40×28 box, scaled down
  const naturalW = colSpan * CELL_PX;
  const scale = 36 / naturalW;
  const scaledH = Math.round(CELL_PX * scale);
  return /*#__PURE__*/React.createElement("svg", {
    width: 36,
    height: scaledH,
    viewBox: `0 0 ${naturalW} ${CELL_PX}`,
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement(ShelfSVG, {
    colSpan: colSpan,
    rowSpan: 1,
    mini: true
  }));
}

// ── BoxSVG — geometry from box.svg ────────────────────────────────────────
// Box occupies rowSpan=3. Peg row = row 0 (top), pegs at CELL_PX/2 from top.
// Layout (all px, CELL_PX=34):
//   pegCY       = 17  (CELL_PX/2, centre of top row)
//   top plank   y=10.55  h=3.06  (above bracket centre, near top of row 0)
//   bottom plank y=78.54  h=3.06  (near bottom of row 2)
//   brackets    y=0   h=91.79  w=3.06
//   medium: pegCX_R = CELL_PX/2 + 3*CELL_PX = 119  (colSpan=4, pegs 3 apart)
//   large:  pegCX_R = CELL_PX/2 + 4*CELL_PX = 153  (colSpan=5, pegs 4 apart)

function BoxSVG({
  colSpan,
  rowSpan = 3,
  opacity = 1
}) {
  const W = colSpan * CELL_PX;
  const H = rowSpan * CELL_PX;
  const pegCX_L = CELL_PX / 2;
  const pegCX_R = W - CELL_PX / 2;
  const pegCY = CELL_PX / 2; // top row centre
  const pegR = 3.23;

  // Plank x extents (same offset as shelf: 11.89px beyond peg on each side)
  const plankX1 = pegCX_L - 11.89;
  const plankX2 = pegCX_R + 11.89;
  const plankW = plankX2 - plankX1;
  const plankH = 3.06;
  const topPlankY = 10.55;
  const botPlankY = H - (3 * CELL_PX - 78.54); // mirrors bottom position

  // Brackets: left at x=20.40 (=bX offset), right mirrored
  const bW = 3.06;
  const bX_L = pegCX_L + 3.40 - bW / 2;
  const bX_R = pegCX_R - 3.40 - bW / 2;
  const bY = 0.01;
  const bH = botPlankY + plankH - bY + (topPlankY - bY); // bottom protrusion matches top

  const face = "#DFC872";
  const edge = "#B8883A";
  const pegC = "#CBA055";
  const hi = "#EDD888";
  const uid = `bx${colSpan}`;
  return /*#__PURE__*/React.createElement("svg", {
    width: W,
    height: H,
    viewBox: `0 0 ${W} ${H}`,
    style: {
      display: "block",
      opacity,
      overflow: "visible"
    },
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("filter", {
    id: uid,
    x: "-20%",
    y: "-20%",
    width: "160%",
    height: "140%"
  }, /*#__PURE__*/React.createElement("feDropShadow", {
    dx: "1.5",
    dy: "2",
    stdDeviation: "1.8",
    floodColor: "rgba(0,0,0,0.38)"
  })), /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_eg`,
    patternUnits: "userSpaceOnUse",
    x: "0",
    y: "0",
    width: bW,
    height: bH
  }, /*#__PURE__*/React.createElement("image", {
    href: ENDGRAIN_B64,
    x: -bH / 2 + bW / 2,
    y: -bW / 2 + bH / 2,
    width: bH,
    height: bW,
    preserveAspectRatio: "xMidYMid slice",
    transform: `rotate(90, ${bW / 2}, ${bH / 2})`
  })), /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_eg_pl`,
    patternUnits: "userSpaceOnUse",
    x: plankX1,
    y: topPlankY,
    width: plankH,
    height: plankH
  }, /*#__PURE__*/React.createElement("image", {
    href: ENDGRAIN_B64,
    x: "0",
    y: "0",
    width: plankH,
    height: plankH,
    preserveAspectRatio: "xMidYMid slice"
  }))), /*#__PURE__*/React.createElement("g", {
    filter: `url(#${uid})`
  }, /*#__PURE__*/React.createElement("rect", {
    x: bX_L,
    y: bY,
    width: bW,
    height: bH,
    fill: `url(#${uid}_eg)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: bX_R,
    y: bY,
    width: bW,
    height: bH,
    fill: `url(#${uid}_eg)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: plankX1,
    y: topPlankY,
    width: plankW,
    height: plankH,
    fill: `url(#${uid}_eg_pl)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: plankX1,
    y: botPlankY,
    width: plankW,
    height: plankH,
    fill: `url(#${uid}_eg_pl)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_peg`,
    patternUnits: "userSpaceOnUse",
    x: pegCX_L - pegR,
    y: pegCY - pegR,
    width: pegR * 2,
    height: pegR * 2
  }, /*#__PURE__*/React.createElement("image", {
    href: ASH_GRAIN_B64,
    x: "0",
    y: "0",
    width: pegR * 2,
    height: pegR * 2,
    preserveAspectRatio: "xMidYMid slice"
  }))), /*#__PURE__*/React.createElement("circle", {
    cx: pegCX_L,
    cy: pegCY,
    r: pegR,
    fill: `url(#${uid}_peg)`,
    stroke: edge,
    strokeWidth: "0.5"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: pegCX_R,
    cy: pegCY,
    r: pegR,
    fill: `url(#${uid}_peg)`,
    stroke: edge,
    strokeWidth: "0.5"
  })));
}

// ── PegSVG — top-down view of a single solid ash peg ─────────────────────
// Occupies 1×1 cell. Rendered as a circle (endgrain face) with a cast shadow.
function PegSVG({
  opacity = 1
}) {
  const W = CELL_PX;
  const H = CELL_PX;
  const cx = W / 2;
  const cy = H / 2;
  const r = HOLE_PX / 2; // match the pegboard hole diameter exactly
  const uid = "peg_svg";
  const edge = "#8B6A3A";
  return /*#__PURE__*/React.createElement("svg", {
    width: W,
    height: H,
    viewBox: `0 0 ${W} ${H}`,
    style: {
      display: "block",
      opacity,
      overflow: "visible"
    },
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("filter", {
    id: uid,
    x: "-30%",
    y: "-30%",
    width: "180%",
    height: "180%"
  }, /*#__PURE__*/React.createElement("feDropShadow", {
    dx: "1",
    dy: "1.5",
    stdDeviation: "1.5",
    floodColor: "rgba(0,0,0,0.40)"
  })), /*#__PURE__*/React.createElement("pattern", {
    id: `${uid}_fill`,
    patternUnits: "userSpaceOnUse",
    x: cx - r,
    y: cy - r,
    width: r * 2,
    height: r * 2
  }, /*#__PURE__*/React.createElement("image", {
    href: ASH_GRAIN_B64,
    x: "0",
    y: "0",
    width: r * 2,
    height: r * 2,
    preserveAspectRatio: "xMidYMid slice"
  }))), /*#__PURE__*/React.createElement("g", {
    filter: `url(#${uid})`
  }, /*#__PURE__*/React.createElement("circle", {
    cx: cx,
    cy: cy,
    r: r,
    fill: `url(#${uid}_fill)`,
    stroke: edge,
    strokeWidth: "0.6"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: cx,
    cy: cy,
    r: r,
    fill: "none",
    stroke: "rgba(0,0,0,0.15)",
    strokeWidth: r * 0.12
  })));
}

// ── Endgrain texture (ply layers, for bracket/plank cross-sections) ─────────
const ENDGRAIN_B64 = "data:image/jpeg;base64,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";

// ── Ash endgrain texture (for pegs) ──────────────────────────────────────────
const ASH_GRAIN_B64 = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAUDBAQEAwUEBAQFBQUGBwwIBwcHBw8LCwkMEQ8SEhEPERETFhwXExQaFRERGCEYGh0dHx8fExciJCIeJBweHx7/2wBDAQUFBQcGBw4ICA4eFBEUHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh7/wAARCABQAFADASIAAhEBAxEB/8QAGgAAAgMBAQAAAAAAAAAAAAAAAwQBAgUABv/EADIQAAIBAwEHAwIFBAMAAAAAAAECAwAEESESEzFBUWFxBSJCMtEjUpHB4WKB8PEzcqH/xAAYAQEBAQEBAAAAAAAAAAAAAAABAAIFA//EABwRAQEBAAIDAQAAAAAAAAAAAAABETFRAhIhQf/aAAwDAQACEQMRAD8A9MSDEIp2YwsMqw4KevalhJLbzAA7EiaBuORUGSWKRoZlKsDhlNEiKTpuJWAlXWJ25iuFrsYmQLMpuoVxJnLxjkeootveJJEILhM81YH3Ke3btSCzSw3BdTsTIcHPA9qLcCK6iM0QKTJ9UY+XjpUsFm2raRW2iyscqy/tXSyreoZEUC5RcsOUg+9K29xlNzc++MnUA8+vY0Ke3lhUOjbyE6hv84H/ADWhoX0+8ljkJQbI+SsKPLFa3K/h5t5+mcox/aujuY5IQlygkI4MBhx96HcxFEMkf4sPJl4r56U6MDS4vfTZ2RiUB0ZTqrjxwrlt4buTfenkRSqMvATx7p9qol0jR7m4UTQtpkn3L46Upc2c6x7+zczRD5L9S+R+9aD1F9aLfRBlYJKo9jHhjoaycrCxhnUqw4k8f9VoRXJXIyQOYPHxUX1tHfxADCSrqjdOx7V5Sngku6uSdtQrtgRv07HzS6sYpdg5WVTjJ50EHYcxygq6nBB0waeCw3iKZDu7hQFWXGhHcVoqTRR3nvicRXONVPBj+xoME0lo5huAVPBtrgfI51dQ9pLiZSj8VYjQ96bLwXcQhuxnAyrj6l7j7VIJYUnIktiEbnH3/poUF0YZdsNsuDjHI9jQp0nsHAkUNDxSRfpb+aYkkt/UEDs+7mHyPBv+33qVTPZw3u1JaERTnjEdAeuKyM3FpcHO9hkB4ZxT7Rz2r7LxkDrnQ+CKOt5DJFurlBMnIOfcPBpmwX6dvl3qG8jHuGN6o6/moVvOFGSMg8apY3BRw+OAwQama2MT72Fg8J1Ug5I7GswD3/p8V+FmRti4Ua5ORJ5796zYC0B2Jk2WHFSNRWnaylD2PEUa7gt7uH3aN8WHFaVKRG6lTdyDbiPAcCueY71nTrNaSKjHeQOfa/Xz0NHZJrWXYlBB5dGooKSRNHOpeJtSOYI6VNOtruN4Tb3Kh4ydOvnPI0rdemyRzl7RywOoBOGArprGZG27d99GeAJww/tzq1rMQdSAwOKfn4E2lzcwho5CQvNCNPJFWaFJxtRPsHkDqv6jhTkskN1GFmHu5NzpOW1ngzJE6ugOCRxFZOHLu0KM80OSudU5jxXWc5Qg6YIxhuY6GnoZARgnBpK+tt2N/CrY+a9O4p4ZTcxhV38GSnyX8v8AFRFPz59KHbXJU5yDpgjkR0q80S6zQgiNvqHSjg4ZcR3duUKnI1GMaGs2SKWE7LrgdeRpqByuNlsZrQjdZNJAPJpzobjC5Z1GNQV5eKhgsuWlPuPB0A/9Fatx6cpO1CQBx2SdKSMDK50ZG5qw40b21CkglgG1/wAkZ+S6/wCqJDeqg+o4xgBgRTMRYZBUDOhB4EUOe1hLLgMg541/vimUWDxyFThgVYcjTUEw2gMDPKl2kt75ARIkc6rox0D9jS8NwofYZgHHH3CoC3toYhv4x7CfcANErrOZkOmMHkdRTUN3EVwxHgnShS21uqGSOaNMDLbTDFGLR1igmOVxCSOXD+KoQ0Um7bQ9KVF1FE+y0kecZB2gQR18UzHfQFAkssZX8pYaeKlsHjmKjAOR0POjo0MybuVQ6jXB4jwazZZIApkhuYpI11ILrkUKP1C3LAb+PI/qFMW4eezkXQYkBPIa/pSzrhtlTjsRTKepwAYM8eBp9Yqz3dlOdmSaInoXGf1zWfXozyj/2Q==";

// ── Plank logo ───────────────────────────────────────────────────────────────
const LOGO_B64 = "data:image/jpeg;base64,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";

// ── Physical colour chart image ──────────────────────────────────────────────
const COLOUR_CHART_B64 = "https://cdn.shopify.com/s/files/1/0572/7082/9107/files/rubio_actual.jpg?v=1782462455";

// ── Plywood texture ────────────────────────────────────────────────────────
const PLYWOOD_B64 = "data:image/jpeg;base64,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";

// ── Utilities ──────────────────────────────────────────────────────────────
function genId() {
  return Math.random().toString(36).slice(2, 9);
}
function doesOverlap(placed, col, row, colSpan, rowSpan, excludeId = null) {
  for (const item of placed) {
    if (item.id === excludeId) continue;
    const acc = getAccDef(item);
    if (!acc) continue;
    // Shelves: only the peg row matters for collision — the bracket above is thin
    // and doesn't physically conflict with adjacent pegs.
    const itemRow = item.type === "shelf" ? item.row + (acc.pegRowOffset || 0) : item.row;
    const itemRowSpan = item.type === "shelf" ? 1 : acc.rowSpan;
    if (col < item.col + acc.colSpan && col + colSpan > item.col && row < itemRow + itemRowSpan && row + rowSpan > itemRow) return true;
  }
  return false;
}

// Resolve colSpan/rowSpan from a placed item
function getAccDef(item) {
  if (item.type === "shelf") {
    const s = SHELF_SIZES.find(s => s.size === item.size);
    return s ? {
      colSpan: s.colSpan,
      rowSpan: s.rowSpan,
      pegRowOffset: s.pegRowOffset
    } : null;
  }
  if (item.type === "box") {
    const b = BOX_SIZES.find(b => b.size === item.size);
    return b ? {
      colSpan: b.colSpan,
      rowSpan: b.rowSpan,
      pegRowOffset: b.pegRowOffset || 0
    } : null;
  }
  if (item.type === "peg") return {
    colSpan: 1,
    rowSpan: 1,
    pegRowOffset: 0
  };
  return null;
}

// ── Component ──────────────────────────────────────────────────────────────
function PegboardConfigurator() {
  const [heightInput, setHeightInput] = useState("800");
  const [widthInput, setWidthInput] = useState("800");
  const [heightMM, setHeightMM] = useState(800);
  const [widthMM, setWidthMM] = useState(800);

  // Active product tab: "shelf" | "box"
  const [activeProduct, setActiveProduct] = useState("shelf");

  // Shelf selection state
  const [selectedSize, setSelectedSize] = useState("small");
  const [selectedDepth, setSelectedDepth] = useState("140mm");
  // Box selection state
  const [selectedBoxSize, setSelectedBoxSize] = useState("medium");
  const [selectedBoxDepth, setSelectedBoxDepth] = useState("140mm");

  // Peg selection state
  const [selectedPegVariant, setSelectedPegVariant] = useState("d140");

  // Stain colour
  const [selectedStain, setSelectedStain] = useState("neutral");
  const [remarks, setRemarks] = useState("");
  const [showColourChart, setShowColourChart] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false); // true = click-to-place mode

  // Zoom & pan
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef(null);
  const viewportRef = useRef(null);
  const [placed, setPlaced] = useState([]);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [activeTab, setActiveTab] = useState("accessories");
  const {
    rows,
    cols
  } = dimsToGrid(heightMM, widthMM);
  const boardPrice = calcPrice(heightMM, widthMM);
  const hVal = parseInt(heightInput, 10);
  const wVal = parseInt(widthInput, 10);
  const hError = heightInput !== "" && (isNaN(hVal) || hVal < MIN_DIM || hVal > MAX_H);
  const wError = widthInput !== "" && (isNaN(wVal) || wVal < MIN_DIM || wVal > MAX_W);

  // Active shelf spec
  const activeShelfSpec = SHELF_SIZES.find(s => s.size === selectedSize);
  const activeShelfPrice = selectedDepth === "140mm" ? activeShelfSpec.price140 : activeShelfSpec.price210;
  const activeBoxSpec = BOX_SIZES.find(b => b.size === selectedBoxSize);
  const activeBoxPrice = selectedBoxDepth === "140mm" ? activeBoxSpec.price140 : activeBoxSpec.price210;

  // Unified active spec for placement (whichever product is selected)
  const activePegSpec = PEG_VARIANTS.find(p => p.id === selectedPegVariant);
  const activePlacingSpec = activeProduct === "shelf" ? activeShelfSpec : activeProduct === "box" ? activeBoxSpec : {
    colSpan: 1,
    rowSpan: 1,
    pegRowOffset: 0
  }; // peg = 1×1
  const activePlacingPrice = activeProduct === "shelf" ? activeShelfPrice : activeProduct === "box" ? activeBoxPrice : activePegSpec.price;
  const activePlacingType = activeProduct; // "shelf" | "box" | "peg"
  const activePlacingDepth = activeProduct === "shelf" ? selectedDepth : activeProduct === "box" ? selectedBoxDepth : activePegSpec.label;
  const applyDims = useCallback((h, w) => {
    setHeightMM(Math.max(MIN_DIM, Math.min(MAX_H, h)));
    setWidthMM(Math.max(MIN_DIM, Math.min(MAX_W, w)));
    setPlaced([]);
    setHistory([]);
    setFuture([]);
    setIsPlacing(false);
  }, []);
  const handleHeightBlur = () => {
    const v = parseInt(heightInput, 10);
    if (!isNaN(v) && v >= MIN_DIM && v <= MAX_H) {
      applyDims(v, widthMM);
      setHeightInput(String(v));
    } else setHeightInput(String(heightMM));
  };
  const handleWidthBlur = () => {
    const v = parseInt(widthInput, 10);
    if (!isNaN(v) && v >= MIN_DIM && v <= MAX_W) {
      applyDims(heightMM, v);
      setWidthInput(String(v));
    } else setWidthInput(String(widthMM));
  };
  const commitPlaced = useCallback(newPlaced => {
    setHistory(h => [...h, placed]);
    setFuture([]);
    setPlaced(newPlaced);
  }, [placed]);
  const handleCellClick = useCallback((col, row) => {
    if (!isPlacing) return;
    const spec = activePlacingSpec;
    if (!spec) return;
    // pegRowOffset: the peg row within the item's span that aligns with the clicked hole
    const offset = spec.pegRowOffset || 0;
    const placedRow = row - offset; // shift so clicked hole aligns with peg row
    if (col + spec.colSpan > cols || placedRow < 0 || placedRow + spec.rowSpan > rows) return;
    // For shelves, collide only on the peg row (clicked hole); for others use full span
    const chkRow = activePlacingType === "shelf" ? row : placedRow;
    const chkRowSpan = activePlacingType === "shelf" ? 1 : spec.rowSpan;
    if (doesOverlap(placed, col, chkRow, spec.colSpan, chkRowSpan)) return;
    commitPlaced([...placed, {
      id: genId(),
      type: activePlacingType,
      size: activePlacingType === "shelf" ? selectedSize : activePlacingType === "box" ? selectedBoxSize : selectedPegVariant,
      depth: activePlacingDepth,
      price: activePlacingPrice,
      col,
      row: placedRow
    }]);
    setIsPlacing(false);
  }, [isPlacing, activePlacingSpec, activePlacingType, activePlacingPrice, activePlacingDepth, selectedSize, selectedBoxSize, selectedPegVariant, placed, cols, rows, commitPlaced]);
  const handleRemove = useCallback(id => {
    commitPlaced(placed.filter(p => p.id !== id));
  }, [placed, commitPlaced]);
  const handleUndo = useCallback(() => {
    if (!history.length) return;
    setFuture(f => [placed, ...f]);
    setHistory(h => h.slice(0, -1));
    setPlaced(history[history.length - 1]);
  }, [history, placed]);
  const handleRedo = useCallback(() => {
    if (!future.length) return;
    setHistory(h => [...h, placed]);
    setFuture(f => f.slice(1));
    setPlaced(future[0]);
  }, [future, placed]);
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") setIsPlacing(false);
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "y" || e.key === "z" && e.shiftKey)) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleUndo, handleRedo]);

  // BOM
  const bom = placed.reduce((acc, item) => {
    const key = `${item.type}_${item.size}_${item.depth}`;
    const typeLabel = item.type === "shelf" ? "Shelf" : item.type === "box" ? "Box" : "Peg";
    if (!acc[key]) acc[key] = {
      label: `${typeLabel} ${item.size} / ${item.depth}`,
      price: item.price,
      count: 0,
      subtotal: 0
    };
    acc[key].count++;
    acc[key].subtotal += item.price;
    return acc;
  }, {});
  const bomList = Object.values(bom);
  const accTotal = bomList.reduce((s, b) => s + b.subtotal, 0);
  const activeStain = STAIN_COLOURS.find(function (c) {
    return c.id === selectedStain;
  });
  const stainPrice = activeStain && activeStain.price ? activeStain.price : 0;
  const grandTotal = boardPrice + stainPrice + accTotal;

  // Preview
  const previewOffset = activePlacingSpec ? activePlacingSpec.pegRowOffset || 0 : 0;
  const previewRow = hoveredCell ? hoveredCell.row - previewOffset : 0;
  const previewChkRow = activeProduct === "shelf" ? hoveredCell?.row ?? 0 : previewRow;
  const previewChkRowSpan = activeProduct === "shelf" ? 1 : activePlacingSpec?.rowSpan ?? 1;
  const canPreview = isPlacing && hoveredCell && activePlacingSpec && hoveredCell.col + activePlacingSpec.colSpan <= cols && previewRow >= 0 && previewRow + activePlacingSpec.rowSpan <= rows && !doesOverlap(placed, hoveredCell.col, previewChkRow, activePlacingSpec.colSpan, previewChkRowSpan);
  const inputStyle = hasError => ({
    width: "100%",
    padding: "8px 10px",
    fontSize: 13,
    fontFamily: "inherit",
    border: `1.5px solid ${hasError ? BRAND.error : BRAND.border}`,
    borderRadius: 6,
    background: hasError ? BRAND.errorBg : BRAND.surface,
    color: BRAND.text,
    outline: "none",
    boxSizing: "border-box"
  });
  const btnStyle = active => ({
    padding: "5px 10px",
    fontSize: 11,
    fontWeight: 600,
    border: `1.5px solid ${active ? BRAND.accent : BRAND.border}`,
    borderRadius: 5,
    cursor: "pointer",
    background: active ? BRAND.accentLight : "transparent",
    color: active ? BRAND.accent : BRAND.muted
  });
  const NEW_LOGO_B64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAA74AAAFSCAYAAADVZwPRAAAACXBIWXMAAAsSAAALEgHS3X78AAAgAElEQVR4nO3d7VVbSbo24HveNf/hjQBNBGYiQBMBTASWIzAdgdURNB1ByxEcO4KRIxiIYOQIDkTQ50ehEdh8SOij9q59XWux3NPjhgeQ9q676tlVf/nzzz8DHIw33GN/qV0AAPSIccRjvyaZ1i6Cfvh/tQsAAACAfRJ8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICm/bV2AfzkNMnxhv/NfA91ADw0fsN/M99xDcDz3jJ+uE5yu4daADpH8D28cZLR/cfyJnWa5GhHn//b/Z/Lm9l1ksX9nwBPWV6Lxlldk5LkbEef/y4/X5MWEYxhE6dZjR1GDz5OdvT5b1Leo4usxg238T4FGvGXP//8s3YNLRulDCTHKTeqdxVrScpNbZFyM5vHzawGb7jH/lK7gAEap1yPlh9duC4tr0nXMUkHySrgju//3NUk1Ft9z+r9OY+V4pqMIx77Ncm0dhH0g+C7W8sVk4v7P3c1C7tPN1mF4C9VKxkGb7jHBN/9Gz/4qD14Xsf3PL4mGVwzFMuxwzj1J6TW8XD8MI/36qEYRzwm+LK2v/z555/jvO3ZrS65Sr0L7nHKzeoiyXmlGnbpW8pg80vK6jC75Yb1mOC7e6M8noDb1WMUtXzN6ppUc2A9SfnZ9tU8u+/yGaX8XPpsWvnrXzz46Pt7dTl+mEfnxj4ZRzzWt+B7mc2fxWc909f+wl9TBkaf9l3Jnp2m3DQO/TUv08bN6qGz+4/fUmZzZxGCoeuOUwLIJP1YKdrE+f3HVcq1aJbDP6YxTvLHgb/mPsx3/PlG6f/4ITn8oHmU1fihD51h61qOH5LSufEl5X27qFUQdMxlyvia3fuwzl9q5Tij8xwu+E5SZjL/neR92gq9P3qX8gb9T8r3PIlZKuiSScrg8n9T3quthd6HjlKuuf/K6np0CMcpYZs2fcpqM7d9G6e8X/+T5GPaCr0/Okn5HpfjB6tcDN0kQu++fMia9+lWgm9SvuF9XlQnKbOWf6TtweVz3qV874uUn/WhBgrAY6OsHu/4I208YrGph9ejfQ+or9J2QGH/44eLlNX2f2W479ffUiboZun/43WwqXHa6Brqot+zweR0S8H3KPtpV5pkFXgNflarLv9OuZFPahYDAzLO49WilrtN1nWSMqDe1wrwRcr1jra9y37GD+OU++T/pB8byx1Cja4NqOk0No/dl88pk99rayn4JmUwON7R5xpH4H3NWVarLpOqlUC7Jhn2atE6TrK6Fo139Dm1OA/LLscPo6zeswLv0w7ZtQG1HKdcC0xU7963vCF7tBZ8k+1blkYpMzP/isC7roeDzknVSqAdk6wm3wye13OScu3+ku13YJ7FYGVoZtlu/HCcsnL8n3jPrmvZtbFI+dkJwLRC6N2fm7xxb6cWg+9J3t6ydJnSfmNV5W0EYNjeOOU6pNvk7c6z2lDnLVo5no7NbDN+GKe85lrY5bqGo5Sf3SLGD7RhnmHuCbRvNynX2zcdb9hi8E1Ky9Immy8dp6wQ/BYzM7uwDMDXsYkFrOs0q/ZIN8vtHaVc0+fZbPV3FC3OQ/Yxm60kHKdsgKZLbDeOsppAP/QxlbArs7iP78NdysTYm0Jv0m7wTdYfuIxTLrBm93fvXXbXdgitWj5L+u9oj9yHs5RJuHUH0bOYAB26WdZruV1OVn3cZzEDdZKyKdg8TpGgX2axKeI+3GXVWfNmLQffdXZpnKYEM4Oc/Vq2HU4r1wFdM0mZeHOT3K+jlEH01St/7zImHyivl9krf2cSrYyHcJYyKTiN53/pvkncz/dlki1Db9J28E3K8yKjJ/79srXZsziHs3x+R/szrHZ9/SMm3g7pY8rP/akB9Cgm51g5z/NdAlfx3j004we6bhJn9e7Lh+zoSKjWg2/y86ztcpc1rc11LNufr2L2lmGaxq6vNZ3l6fbJLxFkeGyWx5Pny0lzrc11PNy13fiBLjmN0Lsvv2SH+24MIfieZbWz52nKjKHWpPo+pvwuPLvDUIxi19eueJfH4Xca9wV+9rDl2aR5d5xnt2d2wzaWz/qze5/z+iNKGxlC8E3KoOYi5YVp18XuOMnq2R1o2SQm3brmKOWecBmTETzvLOUeNY/3b5ccZdU9BrU4q3d/PmcPR5sNJfguNzbxwuymT3n+uTvos2VrpOcBu2l55BG85FOE3q7SPUYtQu/+3GRP53kPJfjSfWfRukRblu1PWiMB9mf56IJzfzmkeUyI7cNN9pgFBF+6ZNm6dPnaX4SOm8RNEeBQ1j2yDHZhFvf3fVie1Xu7ry8g+NJFv2WHO7jBgTnqBKCOl44sg124irN692HvoTcRfOmu9ynP7bh50ReOOgGob3lk2ahuGTRoEvf4fViG3ut9fyHBly778cgR6CpHnQB0x7vY9IrdmsRZvftymQOE3kTwpfuEX7puuYmV530AumN5ZJlNr9jWaTw/vi8fcsDHGwVf+mB58xJ+6RqhF6C7lpteTSrXQX8t7/P27di9X3PgPX0EX/piGX4ndcuA/3IzBOiHP2L8wOaWe3e4z+/e5yTTQ39RwZc+OYqbF90g9AL0i/EDm1ju3XFSuY4WfU2l96LgSx+5eVGT0AvQT8YPrOtLPMa0Dzep+B4UfOmrq3jml8MTegH6TfjlNbOUY7HYrZsc4Kzelwi+9JUNrzg0oRegDcIvz7lK8r52EQ26S9lhvVroTQRf+k345VCOU2aAhV6ANugc40eTJB9rF9Ggu5SV3kXdMgRf+u8o5TmM49qF0KzlBhee9QFoh8lzHrpI6QRg9y6SXNcuIhF8acNJys1L+GUfriL0ArToKKWbx/hh2E5z4PNkB+RDyhi9EwRfWvEuJaDALk3jWR+Alr1L6RxjmOzfsT8f0rEJBcGXlrxPclm7CJoxSfKpdhEA7N1ZOjZA5yDs37E/n9PB95TgS2t+S3mAHrZxGh0EAEPyPnZ6HhL7d+zP53T0vST40iKbXbENM8AAw2Sn5+GYRejdh2/paOhNBF/atNzpGd7CZlYAw2Szq2GYJTmvXUSDblJ2cO4swZdWnaVsTASbmMRmVgBDZrPMtk3jPr8P31MeNbytXMeLBF9a9ilalljfKAY7AJRg1OmVK95kEptW7sNdyvul06E3EXxpn+d9WdcsnusFoJjF+KElF0n+qF1Eg+5SVnqvK9exFsGX1p1EyzOvu0xpjweAZPW8L/13Gr/LfZmkJ6E3EXwZho9xxBHPG8XkCAA/O4+W574bpRxbpKNr9z6kZ5vJCr4Mxax2AXTWLG6IADxtFi3PfXWcEszc43fvl/RwbC34MhRannnKJFqcAXjeUWx82EfHKSu9jifcvc/p6XtC8GVIPqW0vEBSboq9vHADcFDv45GpvrmK0LsPn1MWDXpJ8GVoZrULoDOm0f4EwHpMlPbHLM7q3YeblM1Ae0vwZWjOYtaWsvL/sXYRAPTGu/R4pWtALiP07sNNyvi582f1vkTwZYhmtQugulntAgDonavY6KrLJkl+q11Eg5Zn9fY69CaCL8N0ErO2QzaODa0A2NxRet7q2bBxkj9qF9GgZkJvIvgyXNOYtR2qae0CAOityxg/dM1penaebI+Mk1zXLmJXBF+G6iRmbYdoEqu9ALyd4426ZZRybJHNKnfvQxoKvYngy7CZtR2eae0CAOi993E8Yle8j9C7Dx/S4H4ogi9D5lmdYZmkrPQDwLamtQuAPfk9DYbeRPAFwXc4/K4B2JX30TVGez6n4fGS4MvQHcUOz0MwTjmDEQB2pdmAwCB9TeNjYsEXtCsNwbR2AQA0x14htOImjYfeRPCFpDz3Oa5dBHszip2cAdi9oyQXtYuALd2kobN6XyL4QqFdqV3T2gUA0Kxp7QJgC3cpK73Nh95E8IWl8ziaoEXHMRsPwP7oGqOv7lJeu02d1fsSwRdWJrULYOcu4nw/APZrUrsAeIOLDCj0JoIvPDSpXQA7N6ldAADNu4hNruiXD0nmtYs4tL/WLmDgvt3/eZvHMy7HSU7v/3mU0kbD/p2k3Ly+1C6EnRjFplaH9D3J4v6fFw/+OSm/i9H9P5/GKjzs0sP33nUeP6t3mlUgcz3cn+UmV7PKdcA6PmSgr1XB93C+pcyszPPzjWkdp/cf4/sPYXg/BN92TGoX0LDvWV3LrrP5rPFycm98/+f57kqDpt2l3KO897pH8KUPPmfAr1PBd7++ptygvmT73dKWN7nZ/f8epVxkLyME75KNkNoxqV1AY76nXMtm2f6ZoNusJgKXLu4/3m/5uaE1dynvu1n2+96zJ8J2lptkLuqWAc/6nIGPjTzju3t3SX5N8resZv/2sUX4IslVykX2HykvZrbnTL42jGJCaFe+Jflnys/0MvvbCONLyg35/6dcQ7/v6etAX3xLaUk8zmHee6P7r+e993bGD3TVTQYeehPBd5eWgXeUcqbb4oBfe57yYv5bBOBdcOPqP7/D7X1LmVQb57Dt/7cp19BRyiD87oBfG7rgJqv33uyAX/f2/uuNIgC/1bh2AfCEm3htJhF8d+VzVoG35gHQi5QA/I+UFzlvIzT136R2AT32PatB97xqJatB+K91y4CDuEsJnKfp1nvP5NP6zmN3Z7rle8r9vGY+6QzBdzvLAeIk3XpBzVNunAaLb3OU1a7a9M9xkne1i+ip39ONQfdDyxXgv8eEHu36mhI0Z3XL+Mk05Zrw7ZW/x8q4dgFw7y5lMadLGaUqwfftvqZ7A8QfTVMGi9qVNmfVt7/87jZ3lzKJd5nu3iCvU665HuegJctV3i4PThcpYc5k+nrcg+iCu5T37b72Buglwfdtfk23b1IPLQeLVko2M65dAG82rl1Az9yk+5N4D01SggL03XJgOqtbxtqmKRNkWp9fNq5dAGS/G+L1luC7uQ8pF/8+uY2Vkk2dxXM6fTWuXUCPLDe8WNQtY2OzGIDTbzcprc19G5jOU64Z3nvPO0n53UItH9KfCbWDEnw30/cX0iTC7yY859s/ozjGaF3L0NuHzpWnzGMATj/1/b13He+914xrF8Bg/Zp+Z5W9EnzX1/fQuzRJeT6Z141rF8DGxrUL6Im+D7yXruN5OvqllR1Whd+XjWsXwCB9Tv+6Ug9K8F1Pa7Mnk3jmdx3j2gWwsXHtAnpg+Vxh3wfeS/N45pd+aG2HVRNPzxvXLoDB+RxHOb5K8H3d17Q3e3KbcrMyU/syrc7943f2unHaGXgvzVKOYoIum6R/z/S+Zp7kl9pFdNBJ7BPC4dykbGbFKwTfl92l3dmTRdr93nbFeb794/zel/2S9gbeS5fRyUJ3/Z7kS+0i9uQqzvl9ivEDh9DKo0sHIfi+rKWWpKd8ied9XzOqXQBrG9cuoOO+pQxQWzapXQA84Xva6xz7kS6yn41rF0DzWnt8Yu8E3+d9TX/OtdzGJG5WLzFj2x+j2gV03KR2AQdwnbInA3TJJO0PTG/Tfrjf1Kh2ATRvnv4dR1iV4Pu0uwynV97N6mXj2gWwtlHtAjrs9wzn5ngVk3l0x1Am0ZPy3vO4wcqodgE07zw2mNuI4Pu0qwxnkJiU7/d77SI6yuYU/TGuXUBH3WVYk1u3Gc7EJd03tNfi0L7fl5zVLoBBmMVYdW2C78/u0v5zcE+Z1i6go2yW1B+j2gV01FXab7P80Swm86jvc4Y1iZ6U1W0bXa2MahdA847S7sZ5Oyf4/uxLhjdITMpAUXvg00a1C2AtJ7UL6KhZ7QIqmdUugMEb4iR64r330Kh2AQzCWXRbrEXw/dlQb1SJm9VzRrUL4FWj2gV01BBXnJaGfC2nvpu0e3TYa2bRcbE0ql0AgzGN19urBN/HhnyjSgwUnzOqXQCvGtUuoKOG3P50G8e1Uc/Q76dDvvY8NKpdAINxFAtYrxJ8H5vVLqCyRezI+JRR7QJ4lY0dfnYXg8+hf//UM/TX3qx2AR3h3sQhncWePS8SfB+b1y6gA4Z+s6afnLf8M+9lPwPq+JZh7hXy0HW0OyfuTRzep3jdPUvwXfmeYbc5L81rF9BB49oFwBvMaxfQAbexwyyHN69dQEfMaxcAAzWrXUBXCb4rQm8xr10AvIF2sp/NaxfQEfPaBTA489oFdMS8dgEd4N5EDe+i5flJgu+K4LtihYS+0dbz2F2Gu5vzj1zbOTSvucLPoQQQqOFTdCz+RPBdmdcuoEPcrB4zY0vfeA+v+FlwSN/j+d4l7z2oaxZj2EcE35VF7QI6xE37MTO29I0B58qidgEMyqJ2AR3jpAio5yRanh8RfFcWtQvokHntAoCtmLx6zO6yHIpJp8dci6Cuj9Hy/F+CL0B7DL4fW9QugMEQ9B5b1C4AyJdoeU4i+C7ZzAloicE30AWL2gUAOYojjpIIvjzNahEAALThPMlF7SJqE3x5itUiAABoxywDb3kWfAEAANp2lPK872AJvjxlXLsAAABgp86SXNYuohbBtzitXQAAAMCeTZOMKtdQheBbHNUuAGCHxrUL6JhBP9MEAA8MdpdnwXfFwGjFCvhjjruCfntXuwAA6JCzlJXfQRF8V4S9FZMA0G/j2gV0yKh2AQDQQZ8ysPwj+K4M6hf/inHtAmBD89oFdMyodgEd4toOAE+b1S7gkATfFYOjFT+Lx5xrTN+cROfGkusZADztXQbU8iz4roxrF9ARp7HZ14+uaxcAbzCuXUBHjGsXAAAd9ikDuVcKvisnsTKQDOSFT3NMTvxsXLuADjhO2cADAHjeLAPoFBN8HxvXLqADxrUL6KBF7QJ4lXb0n13ULqAD/AwA4HUnGUDLs+D72GXtAio7TnJeu4gOWtQugFctahfQQbpYBF8AWNfHNL4AJvg+dpLGf+GvmNQuoKMWtQvgVYvaBXTUpHYBFY1iIg8ANvElDbc8C74/m9QuoKKhr3g/Z1G7ANZyU7uADpqk4RvYKya1CwCAnjlKw0ccCb4/e59hnoF5kbLizWPCVH94zvdnRxlmADyOiTwAeIvzNPqokOD7tGntAiowSHzaonYBrG1eu4COuszwVn0v41g2AHirWRocOwi+T3ufYT3rO4kjP57jmJz+WNQuoKNOMqyJrVGG9f0CwK4dpTzv2xTB93lXtQs4kOMMc4V7XYJvfyxqF9BhlxnOIxxXsdoLANs6S2MTyYLv895lGIFwGs/2vkTw7Y957QI6rOnNKh64iJ2cAWBXpmlo4lzwfdmntH0O5jjlzC6edheriH1jM7LnNTdz+4NRhhHuAeBQmpo4F3xfN0+DD3enfE/N9e7vmNXe/vE7e9k0bU7mLa9nWpwBYLeamTgXfF93lPbC73HK92SQ+LJ57QLY2Lx2AR233KyipetZUp7rfVe7CABo1G9pYOJc8F3PuzS0zJ/yvRgkvm5euwA2Nq9dQA+cpK3JvFnKTvwAwP7MahewLcF3fedpY6VkFpu/rGteuwA2tkjyvXYRPfAubYTfWYReADiE3m/8K/hu5jz9HSwu25sNEtfzrXYBvNm8dgE90efwexyhFwAOrdcb/wq+m3uXsoFOn37ppykD3LPKdfTJvHYBvNm8dgE98i5llbxP17NRTOIBQC297YAVfN/mJMm/048dzi5SBome6d2MHa/7a167gJ45Sr+uZ9dxPQOAWk7S05ZnwXc7v6UMsru4WrI83uN/YvfmTd3FsTh9tojzfN9ieT0b1S3jSaO4ngFAV3xMMq5dxKYE3+2dpayWXKUby/7HKbMwi9jE6q2s9vaf3+HbnCX5T8o1pEvXs+u4ngFAl8zSjbHC2gTf3fmYEjavUmfF5GHg/RSrItsQmvrP73A7n1KuJdPUuam5ngFAt52kZ0ccCb67dZQSgP+TMvC+OMDXPE150S1igLgLdxGaWnAdxxpt6yjlmvK/KdeYQ1zPLu6/1v/G9QwAuu48hxkf7MRfaxfQsPP7j2WQmt9/LLb8vMcpPfUX93+ebPn5eEzobceXlIkotvf+/mPX17NRynVsnHJNE3QBqOFbnH7yVrOU+/lt3TJeJ/ju31FWg8ZktXHSdcoLZPnnc8YP/hxF0N03wbcdVxF8d831DIDWfEgZ/13HfektjnK4zrCtCL6Hd5Qyo2RWqXu+R/BtySJld2dH3+yP6xkAffY5q+dUJ0n+Va2SfjtPORbxqnYhL/GML6wIve3p9AUYAKjmc0rYXZon+b1KJW2YpptHIv6X4AsrQlJ7vqS04wIALN3kcehdmsbmmG+1bHnuLMEXim/ZfqMeuuc2VvIBgJWbrPac+NFtSssub3OWDv/8BF8orPa2y+8WAEjKau44L2/E+CXJ14NU06bfUo5b7RzBF2xq1brrlBV9AGC47lJ2Hl7n2J1JPCq1jVntAp4i+IIVwSHwOwaA4bpLWem9XvPv3+bpZ4BZz7uU56U7RfBl6O7S0VkpdupLbFYBAEM1yfqhd0nL83Y+pWMtz4IvQ3eV9Vpe6L9p7QIAgIP7kLc/0jaJludtfElyXLuIJcGXIbuLFtghmcWqLwAMya/ZrrNPy/N2TtKhhQfBlyGz2js809oFAAAH8Tm7ue9red7Oxzx/fNRBCb4MldXeYZrFqi8AtO5zdrtSO4mW523M0oGWZ8GXobLaO1zT2gUAAHtzk+Ryx59Ty/N2TtKBzWQFX4boe4SfIZvFub4A0KKblLbafSxuaHneznnKOcrVCL4M0bR2AVQ3rV0AALBTdynBap8dfZNoed7GLBVbngVfhuZbOtBqQXXzlOd/AID+u0tZ6V3s+etoed7OUSqOwwVfhmbXz3zQX9OYtQWAFoyTXB/oa2l53s55Ko3HBV+G5Pcc7qJI9y1iZ28A6LsPOfz4bhKT59uYJhkd+osKvgzFXTzXyc+mKRthAAD983vqtM5qed5OlZZnwZehmMTxRTxtUrsAAOBNao7ttDxv5ywHbnkWfBmCrykXJ3jKdZJfaxcBAPTOJFqet/FbktNDfTHBl9bdxYoer5tGyzMAsBktz9ubHeoLCb60bhItzqxnUrsAAKB3tDxv510OtA+P4EvLPkeLM+vT8gwAvMUkWp638SkHaHkWfGnVTZzZy+amSb7VLgIA6BUtz9ubJTne5xcQfGnR8rleLc68xUXM2gIAm9HyvJ29tzwLvrToMoc/yJx23KaEXwCATUxi8nwbH5OM9/XJBV9a8zl1DjKnLfMkv9QuAgDoFS3P25tlTy3Pgi8tuYmLDbtzlTKRAgCwLi3P2zlJGYPtnOBLK75nj60RDNZlnO8LAGxmEi3P23ifPTx2JvjSgruUN4fNrNi125QJFTcvAGBdWp63N8uOW54FX1pwEZtZsT/CLwCwKS3P2znKjvftEXzpuw8pGxHBPl1HKz0AsJlJTJxv4zw7XDkXfOmzD7GDM4dznfKaAwBYh5bn7V0lGe3iEwm+9JXQSw2zCL8AwPq0PG9nZy3Pgi995KxeappF+AUA1jeJludtnKWctLEVwZe++T1aRqhvFuEXAFiPluftTZOcbvMJBF/65EN2MNsDOzKL8AsArEfL83a2bnkWfOkLz/TSRbOU16b2JQDgNZMYM2zjXcrK75sIvvSB0EuXzeKcXwDgdVqet/cpb2x5Fnzpsrskf4/QS/ctz/n9XrkOAKDbtDxvb5bkeNP/SPClq25SgsR15TpgXdcpM5A3tQsBADptEp1i23hTy7PgSxd9i9BLP92mhN/PtQsBADpLy/P2PqbkhbUJvnTNrykv4tvKdcA2Jkl+qV0EANBZWp63N8sGLc+CL11xl+Sf2WKnNuiYq5Rn1D33CwA8ZRItz9s4SRlvrUXwpQu+pbSHfqldCOzY8rlfM7rdZWICgFpuY9FnW++TXKzzFwVfalu2Ni/qlgF7c5tyQf4lZnW75nOSUYRfAOq5SlkE4u1mWaPlWfCllpuUNtBp5TrgUK5SVn/d3Lrhl6w2FpnWKwMAtDxv6ShrHH8q+FLDrykBwK7NDM0ipcPB6m89d0k+5PEzQbNY9QWgnkVMwm7rPK/slC34ckhWeaGw+lvH95SJh9kT/9/0kIUAwA+0PG/vKuURpicJvhzCXcoKl1VeWFmkhLB/xmrjIXzNy9egWQw4AKhrEh1h23ix5VnwZd+Wm8esvdU4DMyXlED2a+1CGrWceLvI6+eDT/deDQA8bxH3om2dJbl86v8QfNmXb0n+ljJz9dpgE4ZueZzB31Imi9iNm5RV9XUn3uax6gtAXVqetzdNWVR4RPBl174l+UccUQRvsUiZLPp73PS2sc3jFdOdVwMAm5lEy/M2nmx5Fnyf9iFWXTb1MPDOq1YC/Xed8l76RwTgTS2f5X3r4xXzuP4DUNciJmK39S4//AwF36ctUmZaDDpf9zUCL+zLPOW9pQX6dcvJt4ts320y3bYYANiSluftfcqDlmfB92XzWHV5yl2S31MG4xcReGHfFimTcX9L2QRL+9PK95SdscfZ3bVoERMNANQ3ibTA1RgAAAOhSURBVHv+tmZJjhPBd13zCMBJ2SjmQ8ouzZfxDC8c2iJlNfI45b34tWYxlS1XeEcpO2Pv2jQGGwDUtYgupG39t+VZ8N3MPKu2w98zjEHR96xWd09TZk3s0gz1zVI6Lv6WspHTTdVqDuMuZSX279n/4xWLOIYNgPq0PG/vY5LxX/78889pSv8zK//I+gOqScrg83xfxVRwk/L9z7L5jqi8bFy7gI6Z1y6gMaOU69EkZYazFd9SrkdfctiJt+M8cRxCTy2y+y6dln4+u7KIbqiHRvcfQzbf8ecb7/jz9d0iw3jPud5ubyH4Pm2T4Lt0nDLgvEi5KB3ttqS9ukv5fr/c/7moWAuwG8tr0vj+46RmMW/wNa5JAMCOCL5Pe0vw/dH4wcfZlp9r125SVnKvU75Pq7rQvlFW16TTdG9F+FtW16R9PLMLAAzYX2sX0LB5Hofn0/uPUcrAc5T9r8B8T1kpuX7w5/z5vw40bJHSLjx78O/GWbUijlNWifcdiO+ymnhbxHUJADgAwfdwlgO9H52mDDZ/7N1f/vuXzB/88+2Dz38dG1ABr5s/8+8fXo9+vBa9dm16eC1KVs9f/fjvAQAORvCt7+FAUHsf0AW3WYXi+fN/DQCgHxxnBAAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA0TfAFAACgaYIvAAAATRN8AQAAaJrgCwAAQNMEXwAAAJom+AIAANA0wRcAAICmCb4AAAA07a9JFkm+Va6ja25rFwAAAMBu/B9PIs7l+nPR8wAAAABJRU5ErkJggg==";

  // ── PDF Export ─────────────────────────────────────────────────────────────
  const exportPDF = () => {
    // Load html2canvas then jsPDF, then capture + build PDF
    const loadScript = src => new Promise(resolve => {
      const s = document.createElement("script");
      s.src = src;
      s.onload = resolve;
      document.head.appendChild(s);
    });
    Promise.all([window.html2canvas ? Promise.resolve() : loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"), window.jspdf ? Promise.resolve() : loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js")]).then(() => {
      const boardEl = document.getElementById("board-capture");

      // Temporarily neutralise transform so html2canvas sees the element at scale 1
      const innerTransformed = boardEl.closest('[style*="scale"]');
      const savedTransform = innerTransformed ? innerTransformed.style.transform : null;
      if (innerTransformed) innerTransformed.style.transform = "none";
      html2canvas(boardEl, {
        scale: 2,
        // 2× for sharper output
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false
      }).then(canvas => {
        // Restore transform
        if (innerTransformed && savedTransform !== null) innerTransformed.style.transform = savedTransform;
        const boardImgData = canvas.toDataURL("image/png");
        const boardNatW = canvas.width;
        const boardNatH = canvas.height;
        const {
          jsPDF
        } = window.jspdf;
        const doc = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4"
        });
        const PW = 210;
        const PH = 297;
        const margin = 14;
        const contentW = PW - margin * 2;

        // ── Header ────────────────────────────────────────────────────────────
        doc.setFillColor(26, 23, 20);
        doc.rect(0, 0, PW, 18, "F");

        // Logo image — left-aligned in header
        const logoH = 10; // mm height inside the 18mm header
        const logoW = logoH * 2.8343; // real logo aspect ratio
        doc.addImage(NEW_LOGO_B64, "PNG", margin, (18 - logoH) / 2, logoW, logoH);

        // Date — right-aligned
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const dateStr = new Date().toLocaleDateString("en-SG", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
        doc.text("Generated: " + dateStr, PW - margin, 12, {
          align: "right"
        });

        // ── Board title ────────────────────────────────────────────────────────
        let y = 28;
        doc.setTextColor(26, 23, 20);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("Custom Pegboard Panel", margin, y);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        y += 5;
        doc.text(widthMM + " x " + heightMM + "mm  ·  " + cols + " x " + rows + " holes  ·  100mm pitch  ·  19mm Ø  ·  50mm border", margin, y);
        if (activeStain && activeStain.hex) {
          y += 5;
          doc.text("Stain: " + activeStain.label, margin, y);
        }

        // ── Board screenshot ───────────────────────────────────────────────────
        y += 10;
        const maxImgH = 120;
        const maxImgW = contentW;
        const aspect = boardNatW / boardNatH;
        let imgW = Math.min(maxImgW, maxImgH * aspect);
        let imgH = imgW / aspect;
        if (imgH > maxImgH) {
          imgH = maxImgH;
          imgW = imgH * aspect;
        }
        const imgX = margin + (contentW - imgW) / 2;
        doc.addImage(boardImgData, "PNG", imgX, y, imgW, imgH);

        // Dimension annotations (below and beside the image)
        const arrowY = y + imgH + 4;
        doc.setDrawColor(80, 80, 80);
        doc.setLineWidth(0.2);
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(80, 80, 80);
        doc.line(imgX, arrowY, imgX + imgW, arrowY);
        doc.line(imgX, arrowY - 1.5, imgX, arrowY + 1.5);
        doc.line(imgX + imgW, arrowY - 1.5, imgX + imgW, arrowY + 1.5);
        doc.text(widthMM + "mm", imgX + imgW / 2, arrowY + 3.5, {
          align: "center"
        });
        const arrowX = imgX + imgW + 4;
        doc.line(arrowX, y, arrowX, y + imgH);
        doc.line(arrowX - 1.5, y, arrowX + 1.5, y);
        doc.line(arrowX - 1.5, y + imgH, arrowX + 1.5, y + imgH);
        doc.text(heightMM + "mm", arrowX + 3, y + imgH / 2, {
          align: "left",
          angle: 270,
          baseline: "middle"
        });

        // ── Summary section ────────────────────────────────────────────────────
        y = y + imgH + 16;
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.line(margin, y, PW - margin, y);
        y += 6;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(26, 23, 20);
        doc.text("Order Summary", margin, y);
        y += 7;
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y - 4, contentW, 6, "F");
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text("Item", margin + 2, y);
        doc.text("Qty", margin + contentW * 0.6, y);
        doc.text("Unit Price", margin + contentW * 0.72, y);
        doc.text("Subtotal", PW - margin - 2, y, {
          align: "right"
        });
        y += 5;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(26, 23, 20);
        const lineH = 6;
        const drawRow = (label, qty, unitPrice, subtotal, bold) => {
          if (bold) {
            doc.setFont("helvetica", "bold");
          } else {
            doc.setFont("helvetica", "normal");
          }
          doc.setFontSize(8);
          doc.text(label, margin + 2, y);
          if (qty !== null) doc.text(String(qty), margin + contentW * 0.6, y);
          if (unitPrice !== null) doc.text("$" + unitPrice, margin + contentW * 0.72, y);
          doc.text("$" + subtotal, PW - margin - 2, y, {
            align: "right"
          });
          doc.setDrawColor(235, 235, 235);
          doc.setLineWidth(0.2);
          doc.line(margin, y + 1.5, PW - margin, y + 1.5);
          y += lineH;
        };
        drawRow("Custom Panel (" + widthMM + "x" + heightMM + "mm)", 1, boardPrice, boardPrice, false);
        if (stainPrice > 0 && activeStain) {
          drawRow("Custom Stain — " + activeStain.label, 1, stainPrice, stainPrice, false);
        }
        bomList.forEach(item => {
          drawRow(item.label, item.count, item.price, item.subtotal, false);
        });
        y += 2;
        doc.setFillColor(26, 23, 20);
        doc.rect(margin, y - 4, contentW, 7, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Total (SGD)", margin + 2, y);
        doc.text("$" + grandTotal, PW - margin - 2, y, {
          align: "right"
        });
        y += 10;
        if (remarks.trim()) {
          doc.setTextColor(26, 23, 20);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.text("Remarks:", margin, y);
          y += 5;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          const lines = doc.splitTextToSize(remarks, contentW);
          doc.text(lines, margin, y);
          y += lines.length * 5;
        }

        // ── Footer ─────────────────────────────────────────────────────────────
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.setFont("helvetica", "italic");
        doc.text("This document is for reference and cross-checking purposes only. Prices are indicative and subject to confirmation.", margin, PH - 8);
        doc.text("plankstore.com", PW - margin, PH - 8, {
          align: "right"
        });
        doc.save("plank-pegboard-config-" + dateStr.replace(/ /g, "-") + ".pdf");
      }).catch(err => {
        if (innerTransformed && savedTransform !== null) innerTransformed.style.transform = savedTransform;
        console.error("html2canvas error:", err);
        alert("Could not capture the board image. Please try again.");
      });
    });
  };

  // Load IBM Plex Sans font
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');";
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Reset pan when board dimensions change
  useEffect(() => {
    setPan({
      x: 0,
      y: 0
    });
    setZoom(1);
  }, [heightMM, widthMM]);

  // Always clear drag state on mouseup — even if mouse left the viewport
  useEffect(() => {
    const up = () => {
      setIsDragging(false);
      dragStart.current = null;
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);
  const handleWheel = e => {
    e.preventDefault();
    const delta = -e.deltaY / 600;
    setZoom(z => Math.min(4, Math.max(0.3, z + delta)));
  };
  const handleMouseDown = e => {
    if (e.button !== 2) return; // right-click to pan
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    };
  };
  const handleMouseDownLeft = e => {
    if (e.button !== 2) return; // only right-click pans
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    };
  };
  const handleMouseMove = e => {
    if (!isDragging || !dragStart.current) return;
    setPan({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y
    });
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    dragStart.current = null;
  };
  return /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      background: BRAND.bg,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column"
    }
  }, /*#__PURE__*/React.createElement("header", {
    style: {
      background: BRAND.surface,
      borderBottom: `1px solid ${BRAND.border}`,
      padding: "14px 24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 14
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: LOGO_B64,
    alt: "Plank",
    style: {
      height: 28,
      width: "auto",
      display: "block"
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: 1,
      height: 24,
      background: BRAND.border
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.muted,
      letterSpacing: "0.08em",
      textTransform: "uppercase"
    }
  }, "Pegboard Configurator")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      color: BRAND.muted
    }
  }, "Total: ", /*#__PURE__*/React.createElement("strong", {
    style: {
      color: BRAND.text,
      fontSize: 15
    }
  }, "$", grandTotal.toFixed(0), " SGD"))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flex: 1,
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("aside", {
    style: {
      width: 256,
      background: BRAND.surface,
      borderRight: `1px solid ${BRAND.border}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "16px 16px 14px",
      borderBottom: `1px solid ${BRAND.border}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: BRAND.muted,
      marginBottom: 10
    }
  }, "Board Dimensions"), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 10
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 4
    }
  }, /*#__PURE__*/React.createElement("span", null, "Height (H)"), /*#__PURE__*/React.createElement("span", {
    style: {
      color: BRAND.muted,
      fontWeight: 400
    }
  }, "max 2400mm")), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: MIN_DIM,
    max: MAX_H,
    value: heightInput,
    onChange: e => setHeightInput(e.target.value),
    onBlur: handleHeightBlur,
    onKeyDown: e => e.key === "Enter" && handleHeightBlur(),
    style: {
      ...inputStyle(hError),
      paddingRight: 36
    }
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      position: "absolute",
      right: 10,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 11,
      color: BRAND.muted,
      pointerEvents: "none"
    }
  }, "mm")), hError && /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.error,
      marginTop: 3
    }
  }, "Enter 100–2400mm")), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 10
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 4
    }
  }, /*#__PURE__*/React.createElement("span", null, "Width (W)"), /*#__PURE__*/React.createElement("span", {
    style: {
      color: BRAND.muted,
      fontWeight: 400
    }
  }, "max 1200mm")), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: MIN_DIM,
    max: MAX_W,
    value: widthInput,
    onChange: e => setWidthInput(e.target.value),
    onBlur: handleWidthBlur,
    onKeyDown: e => e.key === "Enter" && handleWidthBlur(),
    style: {
      ...inputStyle(wError),
      paddingRight: 36
    }
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      position: "absolute",
      right: 10,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 11,
      color: BRAND.muted,
      pointerEvents: "none"
    }
  }, "mm")), wError && /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.error,
      marginTop: 3
    }
  }, "Enter 100–1200mm")), /*#__PURE__*/React.createElement("div", {
    style: {
      background: BRAND.accentLight,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 6,
      padding: "7px 10px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.accent
    }
  }, /*#__PURE__*/React.createElement("strong", null, cols, "×", rows), " holes · ", widthMM, "×", heightMM, "mm"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.accent
    }
  }, "$", boardPrice, " SGD")), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: BRAND.muted,
      marginBottom: 6
    }
  }, "Stain Colour"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "7px 10px",
      border: `1.5px solid ${BRAND.border}`,
      borderRadius: 6,
      background: BRAND.surface
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 16,
      height: 16,
      borderRadius: 3,
      flexShrink: 0,
      background: activeStain && activeStain.hex ? activeStain.hex : "#E8D9B5",
      border: "1px solid rgba(0,0,0,0.15)"
    }
  }), /*#__PURE__*/React.createElement("select", {
    value: selectedStain,
    onChange: function (e) {
      setSelectedStain(e.target.value);
    },
    style: {
      flex: 1,
      border: "none",
      background: "transparent",
      fontSize: 12,
      fontWeight: 500,
      color: BRAND.text,
      cursor: "pointer",
      outline: "none",
      fontFamily: "inherit"
    }
  }, STAIN_COLOURS.map(function (c) {
    return /*#__PURE__*/React.createElement("option", {
      key: c.id,
      value: c.id
    }, c.label);
  }))), stainPrice > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.accent,
      marginTop: 4
    }
  }, "Stain add-on: +$", stainPrice, " SGD"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.muted,
      marginTop: 6,
      lineHeight: 1.5,
      fontStyle: "italic"
    }
  }, "Rendered stain colours are for reference only, please refer to", " ", /*#__PURE__*/React.createElement("span", {
    onClick: () => setShowColourChart(true),
    style: {
      color: BRAND.accent,
      cursor: "pointer",
      textDecoration: "underline"
    }
  }, "physical colour chart"), "."))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      borderBottom: `1px solid ${BRAND.border}`,
      flexShrink: 0
    }
  }, ["accessories", "summary"].map(tab => /*#__PURE__*/React.createElement("button", {
    key: tab,
    onClick: () => setActiveTab(tab),
    style: {
      flex: 1,
      padding: "10px 4px",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      background: "transparent",
      border: "none",
      borderBottom: activeTab === tab ? `2px solid ${BRAND.accent}` : "2px solid transparent",
      color: activeTab === tab ? BRAND.accent : BRAND.muted,
      cursor: "pointer"
    }
  }, tab === "accessories" ? "Accessories" : `Summary${bomList.length ? ` (${bomList.length})` : ""}`))), activeTab === "accessories" && /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: "auto",
      padding: "14px 14px 16px"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5,
      marginBottom: 14
    }
  }, [{
    id: "shelf",
    label: "Shelf"
  }, {
    id: "box",
    label: "Box"
  }, {
    id: "peg",
    label: "Peg"
  }].map(p => /*#__PURE__*/React.createElement("button", {
    key: p.id,
    onClick: () => {
      setActiveProduct(p.id);
      setIsPlacing(false);
    },
    style: {
      ...btnStyle(activeProduct === p.id),
      flex: 1,
      padding: "7px 0",
      fontSize: 12
    }
  }, p.label))), activeProduct === "shelf" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 10
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 6
    }
  }, "Size"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5
    }
  }, SHELF_SIZES.map(s => /*#__PURE__*/React.createElement("button", {
    key: s.size,
    onClick: () => setSelectedSize(s.size),
    style: btnStyle(selectedSize === s.size)
  }, s.label)))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 6
    }
  }, "Depth"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5
    }
  }, SHELF_DEPTHS.map(d => /*#__PURE__*/React.createElement("button", {
    key: d,
    onClick: () => setSelectedDepth(d),
    style: btnStyle(selectedDepth === d)
  }, d)))), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "#FAFAF8",
      border: `1.5px solid ${BRAND.border}`,
      borderRadius: 8,
      padding: "12px",
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.text
    }
  }, activeShelfSpec.label, " / ", selectedDepth), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.muted,
      marginTop: 2
    }
  }, "W", activeShelfSpec.externalW, " × H70mm · ", activeShelfSpec.colSpan, " holes wide")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 14,
      fontWeight: 700,
      color: BRAND.accent
    }
  }, "$", activeShelfPrice)), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "#FFFFFF",
      borderRadius: 4,
      padding: "8px 4px 4px",
      display: "flex",
      justifyContent: "center"
    }
  }, /*#__PURE__*/React.createElement(ShelfSVG, {
    colSpan: activeShelfSpec.colSpan,
    rowSpan: activeShelfSpec.rowSpan
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.muted,
      marginTop: 6,
      textAlign: "center"
    }
  }, "Includes 2 pegs"))), activeProduct === "box" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 10
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 6
    }
  }, "Size"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5
    }
  }, BOX_SIZES.map(b => /*#__PURE__*/React.createElement("button", {
    key: b.size,
    onClick: () => setSelectedBoxSize(b.size),
    style: btnStyle(selectedBoxSize === b.size)
  }, b.label)))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 6
    }
  }, "Depth"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5
    }
  }, BOX_DEPTHS.map(d => /*#__PURE__*/React.createElement("button", {
    key: d,
    onClick: () => setSelectedBoxDepth(d),
    style: btnStyle(selectedBoxDepth === d)
  }, d)))), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "#FAFAF8",
      border: `1.5px solid ${BRAND.border}`,
      borderRadius: 8,
      padding: "12px",
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.text
    }
  }, activeBoxSpec.label, " / ", selectedBoxDepth), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.muted,
      marginTop: 2
    }
  }, "W", activeBoxSpec.externalW, " × H", activeBoxSpec.externalH, "mm · ", activeBoxSpec.colSpan, " holes wide")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 14,
      fontWeight: 700,
      color: BRAND.accent
    }
  }, "$", activeBoxPrice)), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "#FFFFFF",
      borderRadius: 4,
      padding: "4px 4px 4px",
      display: "flex",
      justifyContent: "center"
    }
  }, /*#__PURE__*/React.createElement(BoxSVG, {
    colSpan: activeBoxSpec.colSpan,
    rowSpan: activeBoxSpec.rowSpan
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.muted,
      marginTop: 6,
      textAlign: "center"
    }
  }, "Includes 2 pegs"))), activeProduct === "peg" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.text,
      marginBottom: 6
    }
  }, "Variant"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 5
    }
  }, PEG_VARIANTS.map(v => /*#__PURE__*/React.createElement("button", {
    key: v.id,
    onClick: () => setSelectedPegVariant(v.id),
    style: btnStyle(selectedPegVariant === v.id)
  }, v.label)))), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "#FAFAF8",
      border: `1.5px solid ${BRAND.border}`,
      borderRadius: 8,
      padding: "12px",
      marginBottom: 12
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 10
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.text
    }
  }, activePegSpec.desc), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.muted,
      marginTop: 2
    }
  }, "Solid ash · 1 hole")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 14,
      fontWeight: 700,
      color: BRAND.accent
    }
  }, "$", activePegSpec.price)), /*#__PURE__*/React.createElement("div", {
    style: {
      background: "#FFFFFF",
      borderRadius: 4,
      padding: "8px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }
  }, /*#__PURE__*/React.createElement(PegSVG, null)), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.muted,
      marginTop: 6,
      textAlign: "center"
    }
  }, "Click any hole to place"))), /*#__PURE__*/React.createElement("button", {
    onClick: () => setIsPlacing(p => !p),
    style: {
      width: "100%",
      padding: "10px",
      background: BRAND.accent,
      color: "#fff",
      border: "none",
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer"
    }
  }, isPlacing ? "✕  Cancel placement" : `+ Place ${activeProduct} on board`), isPlacing && /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.accent,
      textAlign: "center",
      marginTop: 6
    }
  }, "Click a hole on the board · Esc to cancel")), activeTab === "summary" && /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      overflowY: "auto",
      padding: "12px 12px 16px"
    }
  }, bomList.length === 0 ? /*#__PURE__*/React.createElement("div", {
    style: {
      color: BRAND.muted,
      fontSize: 12,
      textAlign: "center",
      marginTop: 24,
      lineHeight: 1.7
    }
  }, "No accessories placed yet.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      padding: "8px 0",
      borderBottom: `1px solid ${BRAND.border}`
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: BRAND.text
    }
  }, "Custom Panel"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.muted
    }
  }, widthMM, "×", heightMM, "mm")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.text
    }
  }, "$", boardPrice)), stainPrice > 0 && activeStain && /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: `1px solid ${BRAND.border}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 7
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: 12,
      height: 12,
      borderRadius: 2,
      background: activeStain.hex,
      border: "1px solid rgba(0,0,0,0.15)",
      flexShrink: 0
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: BRAND.text
    }
  }, "Custom Stain — ", activeStain.label)), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.text
    }
  }, "$", stainPrice)), bomList.map((item, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    style: {
      display: "flex",
      justifyContent: "space-between",
      padding: "8px 0",
      borderBottom: `1px solid ${BRAND.border}`
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 600,
      color: BRAND.text
    }
  }, item.label), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 11,
      color: BRAND.muted
    }
  }, "×", item.count, " @ $", item.price, " each")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.text
    }
  }, "$", item.subtotal))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0 0",
      marginTop: 4
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 13,
      fontWeight: 700,
      color: BRAND.text
    }
  }, "Total"), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 14,
      fontWeight: 700,
      color: BRAND.accent
    }
  }, "$", grandTotal, " SGD")))), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "12px 12px 0",
      borderTop: `1px solid ${BRAND.border}`,
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: BRAND.muted,
      marginBottom: 6
    }
  }, "Remarks"), /*#__PURE__*/React.createElement("textarea", {
    value: remarks,
    onChange: function (e) {
      setRemarks(e.target.value);
    },
    placeholder: "Any specific instructions or notes...",
    rows: 3,
    style: {
      width: "100%",
      padding: "8px 10px",
      fontSize: 12,
      fontFamily: "inherit",
      color: BRAND.text,
      border: `1.5px solid ${BRAND.border}`,
      borderRadius: 6,
      background: BRAND.surface,
      resize: "vertical",
      outline: "none",
      lineHeight: 1.5,
      boxSizing: "border-box"
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: "12px 12px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: exportPDF,
    style: {
      width: "100%",
      padding: "10px",
      background: "transparent",
      color: BRAND.text,
      border: `1.5px solid ${BRAND.border}`,
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 14
    }
  }, "↓"), " Export PDF"))), /*#__PURE__*/React.createElement("main", {
    style: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 20px",
      borderBottom: `1px solid ${BRAND.border}`,
      background: BRAND.surface,
      flexShrink: 0
    }
  }, isPlacing && /*#__PURE__*/React.createElement("div", {
    style: {
      background: BRAND.accentLight,
      border: `1.5px solid ${BRAND.accent}`,
      borderRadius: 6,
      padding: "5px 12px",
      fontSize: 12,
      color: BRAND.accent,
      fontWeight: 500
    }
  }, "Placing: ", /*#__PURE__*/React.createElement("strong", null, activeProduct === "shelf" ? `Shelf ${activeShelfSpec.label}` : activeProduct === "box" ? `Box ${activeBoxSpec.label}` : `Peg ${activePegSpec.label}`), activePlacingDepth ? ` / ${activePlacingDepth}` : "", " — click a hole · Esc to cancel"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginLeft: "auto"
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 11,
      color: BRAND.muted,
      whiteSpace: "nowrap"
    }
  }, "Zoom"), /*#__PURE__*/React.createElement("button", {
    onClick: () => setZoom(z => Math.max(0.3, z - 0.25)),
    style: {
      width: 24,
      height: 24,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 4,
      background: "transparent",
      cursor: "pointer",
      fontSize: 14,
      color: BRAND.text,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "−"), /*#__PURE__*/React.createElement("input", {
    type: "range",
    min: "30",
    max: "400",
    step: "5",
    value: Math.round(zoom * 100),
    onChange: e => setZoom(parseInt(e.target.value) / 100),
    style: {
      width: 120,
      accentColor: BRAND.accent
    }
  }), /*#__PURE__*/React.createElement("button", {
    onClick: () => setZoom(z => Math.min(4, z + 0.25)),
    style: {
      width: 24,
      height: 24,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 4,
      background: "transparent",
      cursor: "pointer",
      fontSize: 14,
      color: BRAND.text,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "+"), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 12,
      color: BRAND.muted,
      minWidth: 36,
      textAlign: "right"
    }
  }, Math.round(zoom * 100), "%"), /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      setZoom(1);
      setPan({
        x: 0,
        y: 0
      });
    },
    style: {
      padding: "3px 8px",
      fontSize: 11,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 4,
      background: "transparent",
      cursor: "pointer",
      color: BRAND.muted
    }
  }, "Reset")), /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: 10,
      color: BRAND.muted,
      whiteSpace: "nowrap"
    }
  }, "Scroll to zoom · Right-click drag to pan")), /*#__PURE__*/React.createElement("div", {
    ref: viewportRef,
    onWheel: handleWheel,
    onMouseDown: handleMouseDownLeft,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp,
    onContextMenu: function (e) {
      e.preventDefault();
    },
    style: {
      flex: 1,
      overflow: "hidden",
      position: "relative",
      cursor: isDragging ? "grabbing" : isPlacing ? "crosshair" : "default"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`,
      transformOrigin: "center center",
      transition: isDragging ? "none" : "transform 0.05s ease-out"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 6
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: 1,
      width: 24,
      background: BRAND.muted,
      opacity: 0.4
    }
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 11,
      color: BRAND.muted
    }
  }, widthMM, "mm wide"), /*#__PURE__*/React.createElement("div", {
    style: {
      height: 1,
      width: 24,
      background: BRAND.muted,
      opacity: 0.4
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 11,
      color: BRAND.muted,
      transform: "rotate(-90deg)",
      whiteSpace: "nowrap",
      display: "block"
    }
  }, heightMM, "mm tall")), /*#__PURE__*/React.createElement("div", {
    id: "board-capture",
    style: {
      position: "relative",
      background: "#E8D5A3",
      backgroundImage: ["repeating-linear-gradient(180deg, transparent 0px, transparent 5px, rgba(160,120,60,0.07) 5px, rgba(160,120,60,0.07) 6px)", "repeating-linear-gradient(180deg, transparent 0px, transparent 18px, rgba(140,100,40,0.055) 18px, rgba(140,100,40,0.055) 22px)", "repeating-linear-gradient(90deg, transparent 0px, transparent 40px, rgba(255,240,200,0.08) 40px, rgba(255,240,200,0.08) 42px)", "linear-gradient(145deg, rgba(255,252,240,0.35) 0%, transparent 55%)"].join(", "),
      borderRadius: 0,
      border: "1.5px solid #FFFFFF",
      padding: BORDER_PX,
      boxShadow: "0 4px 24px rgba(100,70,20,0.18)",
      cursor: "inherit",
      userSelect: "none",
      overflow: "hidden"
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: "-50%",
      backgroundImage: `url(${PLYWOOD_B64})`,
      backgroundSize: "50% 50%",
      backgroundRepeat: "repeat",
      transform: "rotate(90deg)",
      filter: activeStain && activeStain.hex ? "grayscale(100%)" : "none",
      pointerEvents: "none",
      zIndex: 0
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: 0,
      boxShadow: "inset 0 0 20px rgba(100,60,10,0.15)",
      pointerEvents: "none",
      zIndex: 1
    }
  }), activeStain && activeStain.hex && /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      inset: 0,
      background: activeStain.hex,
      opacity: activeStain && activeStain.id === "whitewash" ? 0.45 : 0.8,
      mixBlendMode: "multiply",
      pointerEvents: "none",
      zIndex: 1
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, ${CELL_PX}px)`,
      gridTemplateRows: `repeat(${rows}, ${CELL_PX}px)`,
      gap: 0,
      position: "relative",
      zIndex: 2
    }
  }, Array.from({
    length: rows
  }, (_, row) => Array.from({
    length: cols
  }, (_, col) => {
    const isHovered = hoveredCell && hoveredCell.col === col && hoveredCell.row === row;
    return /*#__PURE__*/React.createElement("div", {
      key: `${col}-${row}`,
      onClick: () => handleCellClick(col, row),
      onMouseEnter: () => setHoveredCell({
        col,
        row
      }),
      onMouseLeave: () => setHoveredCell(null),
      style: {
        width: CELL_PX,
        height: CELL_PX,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        zIndex: 1
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: HOLE_PX,
        height: HOLE_PX,
        borderRadius: "50%",
        background: isHovered && isPlacing ? BRAND.accent : "#1A0F05",
        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.65), inset 0 0 0 1px rgba(0,0,0,0.15)",
        transition: "background 0.1s"
      }
    }));
  })), canPreview && activePlacingSpec && /*#__PURE__*/React.createElement("div", {
    style: {
      position: "absolute",
      left: hoveredCell.col * CELL_PX,
      top: previewRow * CELL_PX,
      width: activePlacingSpec.colSpan * CELL_PX,
      height: activePlacingSpec.rowSpan * CELL_PX,
      zIndex: 10,
      pointerEvents: "none"
    }
  }, activeProduct === "shelf" ? /*#__PURE__*/React.createElement(ShelfSVG, {
    colSpan: activePlacingSpec.colSpan,
    rowSpan: activePlacingSpec.rowSpan,
    opacity: 0.7
  }) : activeProduct === "box" ? /*#__PURE__*/React.createElement(BoxSVG, {
    colSpan: activePlacingSpec.colSpan,
    rowSpan: activePlacingSpec.rowSpan,
    opacity: 0.7
  }) : /*#__PURE__*/React.createElement(PegSVG, {
    opacity: 0.7
  })), placed.map(item => {
    const spec = item.type === "shelf" ? SHELF_SIZES.find(s => s.size === item.size) : item.type === "box" ? BOX_SIZES.find(b => b.size === item.size) : {
      colSpan: 1,
      rowSpan: 1
    };
    if (!spec) return null;
    return /*#__PURE__*/React.createElement("div", {
      key: item.id,
      title: isPlacing ? "" : "Click to remove",
      onClick: e => {
        if (!isPlacing) {
          e.stopPropagation();
          handleRemove(item.id);
        }
      },
      style: {
        position: "absolute",
        left: item.col * CELL_PX,
        top: item.row * CELL_PX,
        width: spec.colSpan * CELL_PX,
        height: spec.rowSpan * CELL_PX,
        zIndex: 20,
        cursor: isPlacing ? "default" : "pointer"
      }
    }, item.type === "shelf" ? /*#__PURE__*/React.createElement(ShelfSVG, {
      colSpan: spec.colSpan,
      rowSpan: spec.rowSpan
    }) : item.type === "box" ? /*#__PURE__*/React.createElement(BoxSVG, {
      colSpan: spec.colSpan,
      rowSpan: spec.rowSpan
    }) : /*#__PURE__*/React.createElement(PegSVG, null), !isPlacing && /*#__PURE__*/React.createElement("div", {
      style: {
        position: "absolute",
        top: 1,
        right: 3,
        fontSize: 9,
        color: BRAND.woodDark,
        opacity: 0.6,
        background: "rgba(255,255,255,0.5)",
        borderRadius: 2,
        padding: "0 2px"
      }
    }, "✕"));
  })))), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 10,
      fontSize: 11,
      color: BRAND.muted
    }
  }, cols, "×", rows, " holes · 100mm pitch · 19mm Ø · 50mm border", placed.length > 0 && /*#__PURE__*/React.createElement("span", null, " · ", placed.length, " item", placed.length !== 1 ? "s" : "", " placed")), !isPlacing && placed.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 4,
      fontSize: 11,
      color: BRAND.muted
    }
  }, "Click a placed item to remove it."), /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      gap: 8,
      marginTop: 14
    }
  }, /*#__PURE__*/React.createElement("button", {
    onClick: handleUndo,
    disabled: !history.length,
    style: {
      padding: "7px 16px",
      fontSize: 12,
      background: BRAND.surface,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 6,
      cursor: history.length ? "pointer" : "not-allowed",
      opacity: history.length ? 1 : 0.35,
      color: BRAND.text,
      display: "flex",
      alignItems: "center",
      gap: 5
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 14
    }
  }, "↩"), " Undo"), /*#__PURE__*/React.createElement("button", {
    onClick: handleRedo,
    disabled: !future.length,
    style: {
      padding: "7px 16px",
      fontSize: 12,
      background: BRAND.surface,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 6,
      cursor: future.length ? "pointer" : "not-allowed",
      opacity: future.length ? 1 : 0.35,
      color: BRAND.text,
      display: "flex",
      alignItems: "center",
      gap: 5
    }
  }, "Redo ", /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 14
    }
  }, "↪")), /*#__PURE__*/React.createElement("button", {
    onClick: () => placed.length > 0 && commitPlaced([]),
    style: {
      padding: "7px 16px",
      fontSize: 12,
      background: BRAND.surface,
      border: `1px solid ${BRAND.border}`,
      borderRadius: 6,
      cursor: placed.length > 0 ? "pointer" : "not-allowed",
      opacity: placed.length > 0 ? 1 : 0.35,
      color: BRAND.muted
    }
  }, "Clear Board"))))))), showColourChart && /*#__PURE__*/React.createElement("div", {
    onClick: () => setShowColourChart(false),
    style: {
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(0,0,0,0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "zoom-out"
    }
  }, /*#__PURE__*/React.createElement("div", {
    onClick: function (e) {
      e.stopPropagation();
    },
    style: {
      position: "relative",
      maxHeight: "90vh",
      maxWidth: "90vw"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: COLOUR_CHART_B64,
    alt: "Physical Colour Chart",
    style: {
      maxHeight: "90vh",
      maxWidth: "90vw",
      display: "block",
      borderRadius: 4,
      boxShadow: "0 8px 40px rgba(0,0,0,0.5)"
    }
  }), /*#__PURE__*/React.createElement("button", {
    onClick: () => setShowColourChart(false),
    style: {
      position: "absolute",
      top: 8,
      right: 8,
      background: "rgba(0,0,0,0.5)",
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: 32,
      height: 32,
      fontSize: 18,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }
  }, "✕"))));
}

// Mount the app
const root = ReactDOM.createRoot(document.getElementById("pegboard-root"));
root.render(/*#__PURE__*/React.createElement(PegboardConfigurator, null));

